import { AppLayout } from '@/layouts/AppLayout';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ConfirmButton } from '@/components/ConfirmButton';
import { useAuth } from '@/lib/auth';
import { isConsultantOrSolutionProvider } from '@/lib/permissions';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '@/lib/api/projects';
import { projectConceptsApi } from '@/lib/api/project-concepts';
import {
  isProjectInactiveDays,
  type ProjectDto,
  type ProjectConceptDto,
  ProjectConceptStatus,
  ProjectConceptPriority,
  ProjectStatus,
} from '@paladin-monorepo/common';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { formatDate } from '@/utils/formatDate';
import { formatRange } from '@/utils/formatNumber';
import { toast } from 'sonner';
import { useMemo } from 'react';
import {
  type LucideIcon,
  Plus, ArrowUpRight, ArrowRight, Pencil, Trash2, Check, X, Eye,
  FileText, Zap, TrendingUp, Wallet,
  Activity, CheckCircle2, Clock,
} from 'lucide-react';
import { formatPrice } from '@/utils/formatPrice';

type Tab = 'concepts' | 'active' | 'archive';

function projectEscrowCell(p: ProjectDto): string {
  const { milestones, currency } = p;
  if (!milestones.length) return '-';
  const hasAnyPrice = milestones.some(
    (m) => m.price != null && Number.isFinite(m.price),
  );
  if (!hasAnyPrice) return '-';
  const total = milestones.reduce((sum, m) => {
    const n = m.price;
    if (n == null || !Number.isFinite(n)) return sum;
    return sum + n;
  }, 0);
  if (!Number.isFinite(total)) return '-';
  return `${formatPrice(total, currency)} / x`;
}

const ACTIVE_STATUSES: ProjectStatus[] = [
  ProjectStatus.Contracting,
  ProjectStatus.Kickoff,
  ProjectStatus.InProgress,
  ProjectStatus.Closure,
];

function MetricCard({
  title,
  value,
  subValue,
  icon: Icon,
}: {
  title: string;
  value: string | number;
  subValue?: string;
  icon: LucideIcon;
}) {
  return (
    <Card className="py-4">
      <CardContent className="flex flex-col justify-between gap-2">
        <div className="flex items-start justify-between">
          <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
          <Icon className="size-5 text-muted-foreground" />
        </div>
        <div>
          <div className="text-2xl font-bold">{value}</div>
          {subValue && <div className="text-xs text-muted-foreground mt-1 font-medium">{subValue}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

export function ProjectsListPage() {
  const { t } = useTranslation();
  const { user } = useAuth({ middleware: 'auth', tmp: 'ProjectsListPage' });
  const isSupplier = isConsultantOrSolutionProvider(user);
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab') as Tab;
  const activeTab: Tab = isSupplier && (rawTab === 'concepts' || !rawTab)
    ? 'active'
    : (rawTab || 'concepts');

  const setTab = (tab: Tab) => {
    setSearchParams({ tab });
  };

  const tabs: { id: Tab; label: string }[] = isSupplier
    ? [
        { id: 'active', label: t('projects.activeTitle') },
        { id: 'archive', label: t('projects.archiveTitle') },
      ]
    : [
        { id: 'concepts', label: t('projectConcepts.title') },
        { id: 'active', label: t('projects.activeTitle') },
        { id: 'archive', label: t('projects.archiveTitle') },
      ];

  return (
    <AppLayout breadcrumbs={[{ href: '/projects', children: t('projects.title') }]}>
      <div className="container mx-auto p-6">
        <PageHeader
          className="mb-6"
          title={t('projects.title')}
          description={t('projects.description')}
          actions={
            <div className="flex gap-1 rounded-lg border bg-muted p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setTab(tab.id)}
                  className={`rounded-md px-4 py-1.5 text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'border bg-card text-brand-secondary shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          }
        />

        {activeTab === 'concepts' && <ConceptsTab />}
        {activeTab === 'active' && <ProjectsTab filter={ACTIVE_STATUSES} />}
        {activeTab === 'archive' && <ProjectsTab filter={[ProjectStatus.Completed]} />}
      </div>
    </AppLayout>
  );
}

function ConceptsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: concepts, isLoading } = useQuery<ProjectConceptDto[]>({
    queryKey: ['project-concepts'],
    queryFn: () => projectConceptsApi.list(),
  });

  const { data: projects } = useQuery<ProjectDto[]>({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const projectByInquiryId = useMemo(() => {
    const map = new Map<string, string>();
    projects?.forEach((p) => {
      if (p.inquiryId) map.set(p.inquiryId, p.id);
    });
    return map;
  }, [projects]);

  const getConceptLinkTarget = (concept: ProjectConceptDto): string | null => {
    if (concept.projectId) return `/projects/${concept.projectId}/workspace`;
    if (!concept.inquiryId) return null;
    const projectId = projectByInquiryId.get(concept.inquiryId);
    if (projectId) return `/projects/${projectId}/workspace`;
    return `/inquiries/${concept.inquiryId}`;
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => projectConceptsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-concepts'] });
      toast.success(t('projectConcepts.messages.deleted'));
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      projectConceptsApi.update(id, { status: status as ProjectConceptDto['status'] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-concepts'] });
      toast.success(t('projectConcepts.messages.statusUpdated'));
    },
  });

  const convertMutation = useMutation({
    mutationFn: (id: string) => projectConceptsApi.convertToProject(id),
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ['project-concepts'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success(t('projectConcepts.messages.converted'));
      navigate(`/projects/${project.id}/workspace`);
    },
    onError: () => {
      toast.error(t('projectConcepts.messages.convertError'));
    },
  });

  const priorityVariant = (p: ProjectConceptDto['priority']) => {
    switch (p) {
      case ProjectConceptPriority.High: return 'error' as const;
      case ProjectConceptPriority.Medium: return 'warning' as const;
      case ProjectConceptPriority.Low: return 'secondary' as const;
    }
  };

  const statusVariant = (s: ProjectConceptDto['status']) => {
    switch (s) {
      case ProjectConceptStatus.New: return 'info' as const;
      case ProjectConceptStatus.Accepted: return 'success' as const;
      case ProjectConceptStatus.Rejected: return 'error' as const;
    }
  };

  const rangeOpts = useMemo(() => ({
    fromLabel: t('projectConcepts.from'),
    toLabel: t('projectConcepts.to'),
  }), [t]);

  const metrics = useMemo(() => {
    if (!concepts?.length) return null;
    const highPriority = concepts.filter((c) => c.priority === ProjectConceptPriority.High).length;
    const savingsMin = concepts.reduce((sum, c) => sum + (c.expectedSavingsMin ?? 0), 0);
    const savingsMax = concepts.reduce((sum, c) => sum + (c.expectedSavingsMax ?? 0), 0);
    const budgetMin = concepts.reduce((sum, c) => sum + (c.budgetMin ?? 0), 0);
    const budgetMax = concepts.reduce((sum, c) => sum + (c.budgetMax ?? 0), 0);
    const currency = concepts[0]?.budgetCurrency ?? 'CZK';
    const savCurrency = concepts[0]?.expectedSavingsCurrency ?? 'CZK';

    return {
      total: concepts.length,
      highPriority,
      savings: formatRange(savingsMin || null, savingsMax || null, { currency: savCurrency, ...rangeOpts }),
      budget: formatRange(budgetMin || null, budgetMax || null, { currency, ...rangeOpts }),
    };
  }, [concepts, rangeOpts]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>{t('generic.loading')}</CardTitle></CardHeader>
        <CardContent><p className="text-muted-foreground">{t('projectConcepts.loading')}</p></CardContent>
      </Card>
    );
  }

  if (!concepts?.length) {
    return (
      <Card>
        <CardHeader><CardTitle>{t('projectConcepts.empty.title')}</CardTitle></CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{t('projectConcepts.empty.description')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {metrics && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <MetricCard
            title={t('projectConcepts.metrics.totalConcepts')}
            value={metrics.total}
            subValue={t('projectConcepts.metrics.totalConceptsSub')}
            icon={FileText}
          />
          <MetricCard
            title={t('projectConcepts.metrics.highPriority')}
            value={metrics.highPriority}
            subValue={t('projectConcepts.metrics.highPrioritySub')}
            icon={Zap}
          />
          <MetricCard
            title={t('projectConcepts.metrics.expectedSavings')}
            value={metrics.savings}
            subValue={t('projectConcepts.metrics.expectedSavingsSub')}
            icon={TrendingUp}
          />
          <MetricCard
            title={t('projectConcepts.metrics.totalBudget')}
            value={metrics.budget}
            subValue={t('projectConcepts.metrics.totalBudgetSub')}
            icon={Wallet}
          />
        </div>
      )}

      <Card>
        <CardHeader className="border-b">
          <CardTitle>{t('projectConcepts.tableTitle')}</CardTitle>
          <CardAction>
            <Button asChild>
              <Link to="/project-concepts/new">
                <Plus className="size-4" />
                {t('projectConcepts.actions.create')}
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('projectConcepts.fields.title')}</TableHead>
                <TableHead>{t('projectConcepts.fields.department')}</TableHead>
                <TableHead>{t('projectConcepts.fields.priority')}</TableHead>
                <TableHead>{t('projectConcepts.fields.expectedSavings')} / {t('projectConcepts.fields.budget')}</TableHead>
                <TableHead>{t('projectConcepts.fields.status')}</TableHead>
                <TableHead className="text-right">{t('projectConcepts.fields.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {concepts.map((c) => {
                const linkTarget = getConceptLinkTarget(c);
                return (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">
                    {linkTarget ? (
                      <Link to={linkTarget} className="text-primary hover:underline">
                        {c.title}
                      </Link>
                    ) : (
                      c.title
                    )}
                  </TableCell>
                  <TableCell>{c.department}</TableCell>
                  <TableCell>
                    <Badge variant={priorityVariant(c.priority)}>
                      {t(`projectConcepts.priority.${c.priority}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-emerald-600">{formatRange(c.expectedSavingsMin, c.expectedSavingsMax, { currency: c.expectedSavingsCurrency, ...rangeOpts })}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{formatRange(c.budgetMin, c.budgetMax, { currency: c.budgetCurrency, ...rangeOpts })}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(c.status)}>
                      {t(`projectConcepts.status.${c.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {c.status === ProjectConceptStatus.New && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            title={t('projectConcepts.actions.accept')}
                            onClick={() => statusMutation.mutate({ id: c.id, status: ProjectConceptStatus.Accepted })}
                          >
                            <Check className="size-4 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title={t('projectConcepts.actions.reject')}
                            onClick={() => statusMutation.mutate({ id: c.id, status: ProjectConceptStatus.Rejected })}
                          >
                            <X className="size-4 text-red-600" />
                          </Button>
                        </>
                      )}
                      {c.status === ProjectConceptStatus.Accepted && !c.inquiryId && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t('projectConcepts.actions.promote')}
                          onClick={() => navigate('/inquiries/new?conceptId=' + c.id, { state: { fromConcept: c } })}
                        >
                          <ArrowUpRight className="size-4 text-blue-600" />
                        </Button>
                      )}
                      {c.status === ProjectConceptStatus.Accepted && !c.projectId && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t('projectConcepts.actions.convertToProject')}
                          disabled={convertMutation.isPending}
                          onClick={() => convertMutation.mutate(c.id)}
                        >
                          <ArrowRight className="size-4 text-emerald-600" />
                        </Button>
                      )}
                      {linkTarget && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title={
                            c.projectId || (c.inquiryId && projectByInquiryId.has(c.inquiryId))
                              ? t('projectConcepts.actions.viewProject')
                              : t('projectConcepts.actions.viewInquiry')
                          }
                          onClick={() => navigate(linkTarget)}
                        >
                          <Eye className="size-4 text-blue-600" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t('projectConcepts.actions.edit')}
                        onClick={() => navigate(`/project-concepts/${c.id}/edit`)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      {c.status === ProjectConceptStatus.New && (
                        <ConfirmButton
                          variant="ghost"
                          size="icon"
                          title={t('projectConcepts.actions.deleteConfirm.title')}
                          description={t('projectConcepts.actions.deleteConfirm.description')}
                          onConfirm={() => deleteMutation.mutateAsync(c.id)}
                        >
                          <Trash2 className="size-4 text-red-600" />
                        </ConfirmButton>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ProjectsTab({ filter }: { filter: ProjectStatus[] }) {
  const { t } = useTranslation();
  const { data: allProjects, isLoading } = useQuery<ProjectDto[]>({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const projects = useMemo(
    () => allProjects?.filter((p) => filter.includes(p.status)),
    [allProjects, filter],
  );

  const metrics = useMemo(() => {
    if (!projects?.length) return null;
    const inProgress = projects.filter((p) => p.status === ProjectStatus.InProgress).length;
    const completed = projects.filter((p) => p.status === ProjectStatus.Completed).length;
    return { total: projects.length, inProgress, completed };
  }, [projects]);

  const statusVariant = (s: ProjectDto['status']) => {
    switch (s) {
      case ProjectStatus.Completed: return 'success' as const;
      case ProjectStatus.InProgress: return 'info' as const;
      default: return 'secondary' as const;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>{t('generic.loading')}</CardTitle></CardHeader>
      </Card>
    );
  }

  if (!projects?.length) {
    return (
      <Card>
        <CardHeader><CardTitle>{t('projects.empty.title')}</CardTitle></CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{t('projects.empty.description')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {metrics && (
        <div className="grid grid-cols-3 gap-4">
          <MetricCard
            title={t('projects.metrics.totalProjects')}
            value={metrics.total}
            subValue={t('projects.metrics.totalProjectsSub')}
            icon={Activity}
          />
          <MetricCard
            title={t('projects.metrics.inProgress')}
            value={metrics.inProgress}
            subValue={t('projects.metrics.inProgressSub')}
            icon={Clock}
          />
          <MetricCard
            title={t('projects.metrics.completed')}
            value={metrics.completed}
            subValue={t('projects.metrics.completedSub')}
            icon={CheckCircle2}
          />
        </div>
      )}

      <Card>
        <CardHeader className="border-b">
          <CardTitle>{t('projects.tableTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('projects.fields.title')}</TableHead>
                <TableHead>{t('projects.fields.status')}</TableHead>
                <TableHead>{t('projects.fields.escrow')}</TableHead>
                <TableHead>{t('projects.fields.nextMilestone')}</TableHead>
                {/* <TableHead>{t('projects.fields.endDate')}</TableHead> */}
                {/* <TableHead>{t('projects.fields.progress')}</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link to={`/projects/${p.id}/workspace`} className="text-primary hover:underline">
                        {p.projectCode ? `${p.projectCode}: ` : ''}{p.title}
                      </Link>
                      {isProjectInactiveDays(p.lastActivityAt, new Date(), 14) && (
                        <Badge variant="outline" className="text-[10px] border-amber-600/50 text-amber-800 dark:text-amber-200">
                          {t('projects.fields.staleActivity')}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.offer?.account?.party?.name}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(p.status)}>
                      {t(`workspace.status.${p.status}`)}
                    </Badge>
                    <span className="block text-xs text-muted-foreground mt-0.5">{t('projects.fields.progress')}: {p.milestones.filter((m) => m.status === 'Completed').length}/{p.milestones.length}</span>
                  </TableCell>
                  <TableCell>
                    {projectEscrowCell(p)}
                  </TableCell>
                  <TableCell>{p.milestones.find((m) => m.status === 'InProgress') ? formatDate(p.milestones.find((m) => m.status === 'InProgress')!.dueDate!) : formatDate(p.startDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
